import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import CardContent from "@mui/material/CardContent";

// Components
import Header from "../../../Components/Header/Header";
import PrimaryInformation from "./PrimaryInformation";
import Seo from "../../../Components/Cms/Seo";
import OGInfo from "../../../Components/Cms/OgInfo";
import Twitterinfo from "../../../Components/Cms/TwitterInfo";
import Editer from "../../../Components/Cms/Schema";

//scss
import classes from "./index.module.scss";

// Context
import { AppContext } from "../../../Context/AppContext";
import { CMS_PAGE_SLUG_PATTERN } from "../../../Helpers/Constants";

import { useCmsPage } from "../../../Context/Cms-page/useCmsPage";

// Services
import CmsPagesApi from "../../../Services/CmsPages/CmsPages";
import { getCmsPagesDetail, createCmsPages, updateCmsPages } from "../../../api/cmsPages";

// Invalid JSON-LD is dropped silently by the storefront, so it has to be caught
// here or the marketer never learns their structured data never shipped.
const isValidSchemaJson = (raw) => {
  if (!raw || raw.trim() === "") return true;
  try {
    // The storefront only emits objects and arrays, so a bare scalar would be
    // accepted here and then silently dropped at render time.
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null;
  } catch {
    return false;
  }
};

const AddPages = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { showToastMessage, modules, setIsLoading } = useContext(AppContext);

  const [updatePageId, setUpdatePageId] = useState("");
  // If TinyMCE fails to load, its onChange never fires and description submits
  // empty, silently erasing the page body. Observed live when the editor was
  // blocked. Keep what was loaded so the save can be refused instead.
  const loadedDescription = useRef("");

  const CmsPagesService = new CmsPagesApi();

  const {
    cmsPageState,
    clearCmsPageState,
    setSeoData,
    checkSeoData,
    setOgInfoData,
    checkOgInfoData,
    setTwitterInfoData,
    checkTwitterInfoData,
    setPageName,
    setPageSlugName,
    setPageDescription,
    setPageTemplate,
    setSchemaData,
    checkSchemaData,
  } = useCmsPage();

  useEffect(() => {
    clearCmsPageState();
    const { updateId } = params;
    if (updateId && updateId !== undefined && updateId !== null) {
      setUpdatePageId(updateId);
      getPageData(updateId);
    }
  }, []);

  const getPageData = async (updateId) => {
    setIsLoading(true);
    const response = await getCmsPagesDetail(updateId);
    if (response && response.status === 200) {
      let tempResponseData = { ...response.data.data };
      setPageName(tempResponseData.title);
      setPageSlugName(tempResponseData.slug);
      setPageDescription(tempResponseData.description);
      loadedDescription.current = tempResponseData.description || "";
      setPageTemplate(tempResponseData.template);
      setSeoData(tempResponseData.seo);
      setOgInfoData(tempResponseData.seo);
      setTwitterInfoData(tempResponseData.seo);
      setSchemaData(tempResponseData.seo);
    } else {
      showToastMessage(undefined, response.data.message, "error", false);
    }
    setIsLoading(false);
  };

  const handleSubmitClick = async (event) => {
    event.preventDefault();

    let validationFlag = true;
    let validationMessage = "Please add required data";
    if (!cmsPageState.title || cmsPageState.title?.trim() === "") {
      validationFlag = false;
      validationMessage = "Title is required";
    } else if (!cmsPageState.slug || cmsPageState.slug?.trim() === "") {
      validationFlag = false;
      validationMessage = "Slug is required";
    } else if (!CMS_PAGE_SLUG_PATTERN.test(cmsPageState.slug.trim())) {
      validationFlag = false;
      validationMessage =
        "Slug must be lowercase letters, numbers and single hyphens, for example red-roses-guide";
    } else if (
      loadedDescription.current &&
      !(cmsPageState.description || "").trim()
    ) {
      validationFlag = false;
      validationMessage =
        "The editor did not load, so saving now would erase this page's content. Reload the page before saving.";
    } else if (!isValidSchemaJson(cmsPageState.seo?.schema)) {
      validationFlag = false;
      validationMessage =
        "Structured Data (JSON-LD) is not valid JSON. Fix it or clear the field.";
    }

    if (validationFlag) {
      let response;
      // delete cmsPageState.seo.id;
      // delete cmsPageState.seo.parent_id;
      if (updatePageId !== "" && updatePageId !== undefined) {
        let postData = {
          title: cmsPageState.title,
          slug: cmsPageState.slug,
          description: cmsPageState.description,
          template: cmsPageState.template,
          seo: cmsPageState.seo,
        };
        response = await updateCmsPages(postData, updatePageId);
      } else {
        let postData = {
          title: cmsPageState.title,
          slug: cmsPageState.slug,
          description: cmsPageState.description,
          template: cmsPageState.template,
          seo: cmsPageState.seo,
        };
        response = await createCmsPages(postData);
      }

      if (response && response.status === 200) {
        showToastMessage(undefined, response.data.message, "success", false);
        clearCmsPageState();
        navigate("/cms/cms-page");
      } else {
        showToastMessage(undefined, response.data.message, "error", false);
      }
    } else {
      showToastMessage(undefined, validationMessage, "error", false);
    }
  };

  return (
    <React.Fragment>
      <Header title="Cms Page" />
      <div className="row">
        <div id="MainContent">
          <div className={`${classes.cardBody}`}>
            <CardContent>
              <PrimaryInformation data={""} />

              <Seo
                data={cmsPageState.seo}
                setSeoData={setSeoData}
                checkSeoData={checkSeoData}
              />
              <OGInfo
                data={cmsPageState.seo}
                setOgInfoData={setOgInfoData}
                checkOgInfoData={checkOgInfoData}
              />
              <Twitterinfo
                data={cmsPageState.seo}
                setTwitterInfoData={setTwitterInfoData}
                checkTwitterInfoData={checkTwitterInfoData}
              />
              <Editer
                data={cmsPageState.seo}
                setSchemaData={setSchemaData}
                checkSchemaData={checkSchemaData}
              />
              <div className={"col-md-6"}>
                <button
                  onClick={handleSubmitClick}
                  className="btn gray-btn d-inline-block mb-1 mr-2"
                >
                  Save
                </button>
              </div>
            </CardContent>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};
export default AddPages;
